/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { useEffect, useState } from 'react';
import classNames from 'classnames';
import { t } from 'i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Col, Drawer, Grid, Row, Tooltip, Typography } from 'antd';

import { ColumnWidthOutlined } from '@ant-design/icons';

// Misc Imports
import { generateRandomString } from '../../utils/string/generateRandomString';
import useContainerResize from '../../utils/hooks/useContainerResize';

// Constant declarations
const { useBreakpoint } = Grid;

const baseClassName = 'side-modal';

/**
 * Side Modal component, usually including a form with a sticky footer
 * @param {boolean} open Whether the modal is open or not
 * @param {function} onClose Function to call when the modal is closed
 * @param {function} onCancel Function to call when the modal is cancelled
 * @param {node} children The content of the modal
 * @param {string} title The title of the modal
 * @param {string} description The description of the modal
 * @param {number} width The width of the modal
 * @param {boolean} hasFullScreenButton Whether it will show a fullscreen button
 * @param {string} bodyClassName The class name to append to the body of the modal
 * @param {string} headerClassName The class name to append to the header of the modal
 * @param {string} footerClassName The class name to append to the footer of the modal
 * @param {string} className The class name to append to the modal
 * @param {boolean} destroyOnClose Whether to destroy the modal when it is closed
 * @param {object} restProps Any other props that would be valid for an Ant Design Drawer component
 */
const SideModal = ({
  open = false,
  onClose,
  children,
  title,
  description,
  bodyClassName,
  headerClassName,
  footerClassName,
  width = 720,
  hasFullScreenButton = false,
  className = '',
  destroyOnClose = false,
  onCancel = () => {},
  ...restProps
}) => {
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const [isFullScreen, setIsFullScreen] = useState(false);
  // Generate a random string for the drawer - this is to not rely solely on the class name, as more than one drawer can exist at the same time (if destroyOnClose=false)
  // We do not want to include numbers in the class name as class names cannot start with a number
  const [classId] = useState(generateRandomString(4, 'abcdefghijklmnopqrstuvwxyz'));

  const { width: drawerWidth, setWidth: setDrawerInitialWidth, startResizing: resizeDrawer } = useContainerResize(null, null, width, true, width, 1200);

  // Append classNames to the header, body and footer dynamically
  // It is important to use the classId to target the correct drawer
  useEffect(() => {
    if (open) {
      if (headerClassName) {
        const header = document.querySelector(`.${classId}.${baseClassName} .ant-drawer-header`);
        if (header && !header.classList.contains(headerClassName)) {
          header.classList.add(headerClassName);
        }
      }

      if (bodyClassName) {
        const body = document.querySelector(`.${classId}.${baseClassName} .ant-drawer-body`);
        if (body && !body.classList.contains(bodyClassName)) {
          body.classList.add(bodyClassName);
        }
      }

      if (footerClassName) {
        const footer = document.querySelector(`.${classId}.${baseClassName} .ant-drawer-footer`);
        if (footer && !footer.classList.contains(footerClassName)) {
          footer.classList.add(footerClassName);
        }
      }
    }
  }, [open, children, restProps]);

  // Keep it separate, don't reset width when children update
  useEffect(() => {
    if (open) {
      if(width) {
        setDrawerInitialWidth(width);
      }
    }
  }, [open, width]);

  const renderHeader = () => (
    <Row wrap={false} gutter={16} align="middle">
      <Col span={20}>
        <Typography.Title level={4} className="mb-0">
          {title}
        </Typography.Title>

        {
          description ? (
            <Typography.Text className="fw-light">
              {description}
            </Typography.Text>
          ) : null
        }
      </Col>

      <Col span={4}>
        {
          hasFullScreenButton && (
            <Tooltip
              title={t('Fullscreen')}
            >
              <Button
                className="float-right"
                type="text"
                onClick={() => {
                  setIsFullScreen(!isFullScreen);
                }}
              >
                <ColumnWidthOutlined className="fs-xxl" />
              </Button>
            </Tooltip>
          )
        }
      </Col>
    </Row>
  );

  return (
    <Drawer
      title={renderHeader()}
      placement="right"
      onClose={onClose}
      onCancel={onCancel}
      open={open}
      destroyOnClose={destroyOnClose}
      width={isMobileOrTabletSize ? '100%' : isFullScreen ? '100%' : drawerWidth}
      className={classNames(baseClassName, classId, {
        [`${baseClassName}--mobile`]: isMobileOrTabletSize,
        [`${baseClassName}--desktop`]: !isMobileOrTabletSize
      }, className)}
      {...restProps}
    >
      <div
        hidden={isMobileOrTabletSize}
        onMouseDown={resizeDrawer}
        className="resize-handle left-0 top-0"
      />
      {children}
    </Drawer>
  );
};

SideModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
  destroyOnClose: PropTypes.bool,
  children: PropTypes.node,
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element
  ]),
  description: PropTypes.string,
  width: PropTypes.number,
  bodyClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  footerClassName: PropTypes.string,
  shouldShowDismissButton: PropTypes.bool,
  hasFullScreenButton: PropTypes.bool,
  className: PropTypes.string
};

export default SideModal;
